import { getClientsMinimal, IClientMinimal } from "@/api";
import { TextField } from "@/components/shared/TextField";
import { Autocomplete } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

interface ClientAutocompleteProps {
    value: { id: number } | null;
    onChange: (client: IClientMinimal | null) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
}

export const ClientAutocomplete = ({
    value,
    onChange,
    label = "Client",
    placeholder = "Select client...",
    required = false,
    disabled = false,
    error = false,
    helperText,
}: ClientAutocompleteProps) => {
    const { data: clients, isPending } = useQuery({
        queryKey: ["clients-minimal"],
        queryFn: getClientsMinimal,
        staleTime: 5 * 60 * 1000,
    });

    const clientOptions = useMemo(() => clients ?? [], [clients]);

    const selectedClient = useMemo(
        () => clientOptions.find((c) => c.id === value?.id) ?? null,
        [value?.id, clientOptions]
    );

    return (
        <Autocomplete
            options={clientOptions}
            value={selectedClient}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, newValue) => onChange(newValue)}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    required={required}
                    error={error}
                    helperText={helperText}
                />
            )}
            size="small"
            autoComplete
            loading={isPending}
            noOptionsText={isPending ? "Loading..." : "No clients found"}
            loadingText="Loading clients..."
        />
    );
};
